<template>
  <div class="flex text-sm font-body">
    <template v-for="(step, index) of steps" :key="step.key">
      <div
        class="flex items-center justify-center px-2 h-9 border-b"
        :class="{
          'w-full': expand,
          [type === 'primary' ? 'border-v-500' : 'border-n-800']:
            currentStepIndex === index,
          'border-transparent': currentStepIndex !== index,
          [type === 'primary' ? 'text-v-500' : 'text-n-800']:
            (index < mostAdvancedStep && step.isDone) ||
            index === currentStepIndex,
          'text-r-300': index < mostAdvancedStep && !step.isDone,
          'cursor-pointer':
            index !== currentStepIndex && index <= mostAdvancedStep
        }"
        @click="onClick(index)"
      >
        <l-icon v-if="step.isDone" class="pr-1" name="check-circle" />
        <div v-else>{{ index + 1 }}.&nbsp;</div>
        <div v-if="!isMobile || index === currentStepIndex">
          {{ step.name }}
        </div>
      </div>
      <div
        class="flex items-center justify-center h-9 px-1"
        :class="{
          [type === 'primary' ? 'text-v-500' : 'text-n-800']:
            index <= currentStepIndex
        }"
      >
        <l-icon v-if="index < steps.length - 1" name="bracket-right" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

import { LIcon } from '@last/core-ui/paprika'

import type { LStep } from './types'

type Props = {
  /**
   * Color style of the steps.
   */
  type?: 'primary' | 'secondary'
  /**
   * The steps to display with properties like key, name and isDone.
   * `isDone` is used to display a check mark icon.
   */
  steps: LStep[]
  /**
   * The index of the current step.
   */
  currentStepIndex?: number
  /**
   * The most advanced step that the user can navigate to.
   * This is useful when you want to prevent the user from navigating to a step that is not yet available.
   */
  mostAdvancedStep?: number
  /**
   * Whether the steps should expand to fill the available space.
   */
  expand?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'primary',
  currentStepIndex: 0,
  mostAdvancedStep: 0,
  expand: false
})

const emit = defineEmits<{
  changeStep: [number]
}>()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smallerOrEqual('sm')

function onClick(index: number): void {
  if (index <= props.mostAdvancedStep || props.steps[index].isDone) {
    emit('changeStep', index)
  }
}
</script>
